import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ErroApi } from '../../api';
import { nomeDoContato, telefoneLegivel } from '../../formato';
import { Aviso, Campo, Dialogo, ErroDaApi } from '../../ui';
import { faltaPara, Rodape } from './JanelasDaConversa';
import { EscolhaDeCliente } from './JanelasDaOportunidade';
import './comercial.css';
import './oportunidades.css';

/**
 * A janela do contato — onda 554 (Parte B · Contatos e conversa ativa,
 * Tarefa 7; spec §3.3, quadro 3 do canvas). Uma janela só para três casos:
 *
 *  · **novo** — o «Novo contato» da lista de Contatos: nasce salvo, do
 *    Comercial (`POST /comercial/contatos`);
 *  · **salvar** — o «Gravar contato» de quem chegou sozinho (pelo WhatsApp,
 *    pela ligação, por número novo): o nome vem sugerido pelo perfil do
 *    WhatsApp, e o contato passa a ser da agenda do sistema
 *    (`POST /comercial/contatos/:id/salvar`; B2: só no sistema);
 *  · **mudar** — o «Mudar» da ficha do contato (`PATCH`), sem o cliente: o
 *    cliente se liga pelo «Ligar a um cliente», com o papel.
 *
 * Os campos: Nome (obrigatório), Cargo, E-mail, WhatsApp, Telefone e, no
 * novo e no salvar, o Cliente (a busca no cadastro; pode ficar em branco).
 * O WhatsApp que já é de outro contato ativo não grava (409): a janela diz
 * de quem é, com a frase da API, e oferece «Abrir o contato existente».
 *
 * A ação da spec §3.3 («salvar na agenda», B2) leva o verbo da casa: «Gravar
 * contato» (P-TXT-09; decisão do controlador da onda 554, correção 1 da
 * Tarefa 7). O modo e a rota da API continuam `salvar`.
 */

/** O nome da ação de gravar na agenda quem chegou sozinho — um lugar só, para a lista, a janela e o painel. */
export const GRAVAR_CONTATO = 'Gravar contato';

export interface ContatoDaJanela {
  id: string;
  nome: string;
  cargo?: string | null;
  email?: string | null;
  telefone?: string | null;
  whatsapp?: string | null;
  /** O nome do perfil do WhatsApp («Carla M.»): a sugestão do nome no salvar. */
  whatsappPerfil?: string | null;
  salvo?: boolean;
}

interface ClienteEscolhido { id: string; razao_social: string; nome_fantasia: string | null }

/** O nome que o banco escreveu com o número não é nome: no salvar, o perfil do WhatsApp sugere. */
const ehNumero = (nome: string) => /^\+?\d{8,15}$/.test(nome.trim());

export function JanelaDoContato({ modo, aberta = true, contato, aoGravar, aoFechar }: {
  modo: 'novo' | 'salvar' | 'mudar';
  /** Aberta por padrão: quem a usa pode montá-la só quando abre. */
  aberta?: boolean;
  contato?: ContatoDaJanela | null;
  aoGravar(id: string): void;
  aoFechar(): void;
}) {
  const navegar = useNavigate();
  const [nome, setNome] = useState('');
  const [cargo, setCargo] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [cliente, setCliente] = useState<ClienteEscolhido | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const c = contato ?? null;

  useEffect(() => {
    if (!aberta) return;
    const sugerido = modo === 'salvar' && c && ehNumero(c.nome) && c.whatsappPerfil ? c.whatsappPerfil : c?.nome ?? '';
    /* O novo vem vazio, ou com o nome do cartão de contato que chegou na conversa (onda 555). */
    setNome(modo === 'novo' ? c?.nome ?? '' : sugerido);
    setCargo(c?.cargo ?? ''); setEmail(c?.email ?? '');
    setTelefone(c?.telefone ?? '');
    /* O WhatsApp no campo como se lê (quadro 3 do canvas); a API normaliza o que vier. */
    setWhatsapp(c?.whatsapp ? telefoneLegivel(c.whatsapp) : '');
    setCliente(null); setErro(null);
  }, [aberta, modo, c?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /* O 409 do WhatsApp de outro contato: a frase da API e, quando a pessoa vê o outro, o atalho. */
  const duplicado = erro instanceof ErroApi && erro.status === 409 && typeof erro.corpo?.contatoId === 'string'
    ? erro.corpo.contatoId : null;

  const falta = faltaPara('gravar', nome.trim().length < 2 ? ['o nome'] : []);
  const gravar = async () => {
    setOcupado(true);
    setErro(null);
    const corpo = {
      nome: nome.trim(), cargo: cargo.trim() || null, email: email.trim() || null,
      telefone: telefone.trim() || null, whatsapp: whatsapp.trim() || null,
      ...(modo !== 'mudar' && cliente ? { clienteId: cliente.id } : {}),
    };
    try {
      if (modo === 'novo') {
        const r = await api<{ id: string }>('/comercial/contatos', { metodo: 'POST', corpo });
        aoGravar(r.id);
      } else {
        await api(modo === 'salvar' ? `/comercial/contatos/${c!.id}/salvar` : `/comercial/contatos/${c!.id}`,
          { metodo: modo === 'salvar' ? 'POST' : 'PATCH', corpo });
        aoGravar(c!.id);
      }
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  };

  const titulo = modo === 'novo' ? 'Novo contato' : modo === 'salvar' ? GRAVAR_CONTATO : 'Mudar o contato';
  const rotulo = modo === 'novo' ? undefined
    : modo === 'salvar' && c?.whatsapp ? `Chegou pelo WhatsApp · ${telefoneLegivel(c.whatsapp)}`
      : c ? nomeDoContato(c.nome) : undefined;
  return (
    <Dialogo aberto={aberta} titulo={titulo} rotulo={rotulo} aoFechar={aoFechar} classe="janela-do-contato">
      <div className="janela-contato">
        <Campo rotulo="Nome" name="nome" value={nome} autoComplete="off" onChange={(e) => setNome(e.target.value)}
          dica={modo === 'salvar' && c?.whatsappPerfil ? `Sugestão do perfil do WhatsApp: «${c.whatsappPerfil}»` : undefined} />
        <div className="janela-linha">
          <Campo rotulo="Cargo" name="cargo" value={cargo} onChange={(e) => setCargo(e.target.value)} />
          <Campo rotulo="E-mail" name="email" type="email" value={email} placeholder="nome@empresa.com.br"
            onChange={(e) => setEmail(e.target.value)} />
          <Campo rotulo="WhatsApp" name="whatsapp" type="tel" inputMode="tel" value={whatsapp} placeholder="(47) 99999-1234"
            onChange={(e) => setWhatsapp(e.target.value)} />
          <Campo rotulo="Telefone" name="telefone" type="tel" inputMode="tel" value={telefone} placeholder="(47) 3333-1234"
            onChange={(e) => setTelefone(e.target.value)} />
        </div>
        {modo !== 'mudar' && (
          <EscolhaDeCliente valor={cliente} aoEscolher={setCliente}
            dica="Busque um cliente do cadastro. Se ainda não for cliente, deixe em branco: ele entra quando a proposta for aceita." />
        )}
        {duplicado ? (
          <Aviso tipo="atencao">
            <span>
              {(erro as ErroApi).message}{' '}
              <button type="button" className="como-link" onClick={() => { aoFechar(); navegar(`/comercial/contato/${duplicado}`); }}>
                Abrir o contato existente
              </button>
            </span>
          </Aviso>
        ) : <ErroDaApi erro={erro} />}
      </div>
      <Rodape acao={modo === 'salvar' ? GRAVAR_CONTATO : 'Gravar'} falta={falta} ocupado={ocupado} aoCancelar={aoFechar}
        aoAgir={() => void gravar()} />
    </Dialogo>
  );
}
